import _ from 'lodash'
import { gql, type ResultOf } from '~/tada/gql'
import { defineStore } from 'pinia'

export type ArchiveFestival = ResultOf<typeof ArchiveIndexQuery>['film_festivals'][number]
export type ArchiveTeam = ResultOf<typeof ArchiveYearQuery>['film_festivals'][number]['teams'][number]
export type ArchiveWinner = ArchiveFestival['winners'][number] & { year: number }

export const AWARD_RANKS: Record<number, { label: string, color: string }> = {
  1: { label: 'Winner', color: 'amber' },
  2: { label: 'Runner-up', color: 'blue-grey' },
  3: { label: 'Nominee', color: 'brown' },
  6: { label: 'Top Ten', color: 'grey' },
}

/** Films sampled per year when picking cover posters; extras cover films without one. */
const COVER_SAMPLE_SIZE = 8

/** Search results shown per year before linking to that year's page. */
export const SEARCH_LIMIT_PER_YEAR = 24

export const festivalYear = (festival: { id: number }) => festival.id + 2000

export const archiveYearPath = (year: number) => `/archive/${year}`
export const archiveTopTenPath = (year: number) => `/archive/${year}/top-10`
export const archiveFilmPath = (year: number, teamId: number) => `/archive/${year}/${teamId}`
export const archiveStudiosPath = '/archive/studios'
export const archiveStudioPath = (studioId: number) => `/archive/studios/${studioId}`

/** Film still from Mux, used as a backdrop and as a stand-in for missing posters. */
export const muxStill = (
  film: { mux_playback_id?: string | null, mux_thumbnail_time?: number | null } | null | undefined,
  width = 640,
) => {
  if (!film?.mux_playback_id) return undefined
  // Without a chosen time Mux uses the middle of the film, which skips the
  // color bars older submissions were required to open and close with.
  const time = film.mux_thumbnail_time != null ? `&time=${film.mux_thumbnail_time}` : ''
  return `https://image.mux.com/${film.mux_playback_id}/thumbnail.webp?width=${width}${time}`
}

/** A studio's record across every year, summarized for ranking and listing. */
export type ArchiveStudio = {
  id: number
  name: string
  /** Festival years with a submitted film, oldest first. */
  years: number[]
  /** Years in which the studio won at least one award. */
  winningYears: number[]
  films: number
  wins: number
  runnerUps: number
  nominations: number
  topTens: number
  poster?: string
}

type StudioSort = ((s: ArchiveStudio) => number | string)[]
const byName = (s: ArchiveStudio) => s.name.toLowerCase()
/** Longest-running first: most festivals entered, then the earliest start. Pass to `_.sortBy`. */
export const byTenure: StudioSort = [s => -s.years.length, s => s.years[0], s => -s.films, byName]
/** Most decorated first: wins, then runner-ups, nominations, and Top Ten finishes. Pass to `_.sortBy`. */
export const byAwards: StudioSort = [s => -s.wins, s => -s.runnerUps, s => -s.nominations, s => -s.topTens, byName]
export const byNewest: StudioSort = [s => -s.years[0], byName]
export const byFilms: StudioSort = [s => -s.films, ...byTenure]
export const byStudioName: StudioSort = [byName]
export const hasPlaced = (s: ArchiveStudio) => s.wins + s.runnerUps + s.nominations + s.topTens > 0

/** Genres were free text for years; fold the obvious duplicates together. */
export const normalizeGenre = (genre?: string | null) => {
  const g = (genre ?? '').trim()
  if (!g || /^(null|na|n\/a|any genre)$/i.test(g)) return null
  return g.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * The Archive loads in small pieces so no page pulls more than it shows: a
 * light index of every festival, one festival's films, one film's details,
 * and cross-year search results.
 */
export const useArchive = defineStore(`archive`, () => {
  const router = useRouter()
  const route = computed(() => router.currentRoute.value)

  const year = computed(() => {
    if (!route.value.path.startsWith('/archive/') || route.value.path.startsWith('/archive/fivefest')) return null
    return parseInt(route.value.params.year as string) || null
  })
  const teamId = computed(() => parseInt(route.value.params.teamId as string) || null)
  const studioId = computed(() => {
    if (!route.value.path.startsWith('/archive/studios/')) return null
    return parseInt(route.value.params.studioId as string) || null
  })

  const { result: indexResult } = useQuery(ArchiveIndexQuery, {}, { clientId: 'anyone' })
  const festivals = computed(() => indexResult.value?.film_festivals ?? [])
  const winners = computed<ArchiveWinner[]>(() => festivals.value.flatMap(festival =>
    festival.winners.map(winner => ({ ...winner, year: festivalYear(festival) }))
  ))
  const filmCount = computed(() => _.sumBy(festivals.value, f => f.teams.length))

  // Each year's cover posters are a random sample drawn once per visit, so no
  // film is permanently the face of its year.
  const coverSample = ref<number[]>([])
  watch(festivals, list => {
    if (coverSample.value.length || !list.length) return
    coverSample.value = list.flatMap(f => _.sampleSize(_.map(f.teams, 'id'), COVER_SAMPLE_SIZE))
  }, { immediate: true })
  const { result: coversResult } = useQuery(
    ArchiveCoversQuery,
    () => ({ ids: coverSample.value }),
    () => ({ clientId: 'anyone', enabled: coverSample.value.length > 0 }),
  )
  const covers = computed<Record<number, string[]>>(() => _.chain(coversResult.value?.film_festivals)
    .keyBy('id')
    .mapValues(f => _.chain(f.covers)
      .sortBy(c => coverSample.value.indexOf(c.id))
      .map(c => c.poster[0]?.file_name)
      .compact()
      .take(3)
      .value()
    )
    .value()
  )

  // Studios are few enough (and their films light enough) to summarize in full
  // for the landing page, the studio list, and search. The home page shares
  // this store but shows no studios, so wait until someone opens the Archive.
  const studiosWanted = ref(false)
  watchEffect(() => {
    if (route.value.path.startsWith('/archive')) studiosWanted.value = true
  })
  const { result: studiosResult } = useQuery(
    ArchiveStudiosQuery,
    {},
    () => ({ clientId: 'anyone', enabled: studiosWanted.value }),
  )
  const studios = computed<ArchiveStudio[]>(() => (studiosResult.value?.studios ?? []).map(studio => {
    const placements = _.countBy(studio.teams.flatMap(t => t.awards), 'rank')
    const yearOf = (t: { film_festival_id: number }) => festivalYear({ id: t.film_festival_id })
    return {
      id: studio.id,
      name: studio.name.trim(),
      years: _.chain(studio.teams).map(yearOf).uniq().sortBy().value(),
      winningYears: _.chain(studio.teams).filter(t => _.some(t.awards, { rank: 1 })).map(yearOf).uniq().value(),
      films: studio.teams.length,
      wins: placements[1] ?? 0,
      runnerUps: placements[2] ?? 0,
      nominations: placements[3] ?? 0,
      topTens: placements[6] ?? 0,
      poster: studio.latest[0]?.poster[0]?.file_name,
    }
  }))

  const { result: studioResult, loading: studioLoading } = useQuery(
    ArchiveStudioQuery,
    () => ({ id: studioId.value ?? 0 }),
    () => ({ clientId: 'anyone', enabled: studioId.value != null }),
  )
  const studio = computed(() => {
    const found = studioResult.value?.studios[0]
    return found && found.id === studioId.value ? found : null
  })
  const studioSummary = computed(() => _.find(studios.value, { id: studioId.value ?? undefined }))

  const searchTerm = computed(() => route.value.path === '/archive' ? String(route.value.query.q ?? '').trim() : '')
  const searching = computed(() => searchTerm.value.length >= 2)
  const { result: searchResult, loading: searchLoading } = useQuery(
    ArchiveSearchQuery,
    // Escape LIKE wildcards so "100%" searches for a literal percent sign.
    () => ({ limit: SEARCH_LIMIT_PER_YEAR, q: `%${searchTerm.value.replace(/[\\%_]/g, '\\$&')}%` }),
    () => ({ clientId: 'anyone', enabled: searching.value }),
  )
  const searchResults = computed(() => {
    if (!searching.value) return []
    return (searchResult.value?.film_festivals ?? [])
      .filter(f => f.teams.length)
      .map(f => ({ year: festivalYear(f), teams: f.teams }))
  })

  const { result: yearResult, loading: yearLoading } = useQuery(
    ArchiveYearQuery,
    () => ({ id: (year.value ?? 0) - 2000 }),
    () => ({ clientId: 'anyone', enabled: year.value != null }),
  )
  const awards = computed(() => yearResult.value?.awards ?? studioResult.value?.awards ?? [])
  const filmFestival = computed(() => {
    const festival = yearResult.value?.film_festivals[0]
    return festival && festivalYear(festival) === year.value ? festival : null
  })
  const teams = computed(() => filmFestival.value?.teams ?? [])

  const { result: filmResult } = useQuery(
    ArchiveFilmQuery,
    () => ({ festivalId: (year.value ?? 0) - 2000, teamId: teamId.value ?? 0 }),
    () => ({ clientId: 'anyone', enabled: year.value != null && teamId.value != null }),
  )
  const team = computed(() => _.find(teams.value, { id: teamId.value ?? undefined }))
  const description = computed(() => {
    const detail = filmResult.value?.film_festivals[0]?.teams[0]
    return detail?.id === teamId.value ? detail?.description : undefined
  })

  const neighbors = computed(() => {
    const i = _.findIndex(teams.value, { id: teamId.value ?? undefined })
    if (i < 0) return { previous: undefined, next: undefined, upNext: [] }

    const n = teams.value.length
    return {
      previous: teams.value[(i - 1 + n) % n],
      next: teams.value[(i + 1) % n],
      upNext: _.chain(teams.value).slice(i + 1).concat(_.slice(teams.value, 0, i)).take(6).value(),
    }
  })

  const findAward = (awardId: number) => _.find(awards.value, { id: awardId })

  /** The year's rank-1 wins, Best Film first, then alphabetically by award. */
  const yearWinners = computed(() => _.chain(teams.value)
    .flatMap(team => team.awards
      .filter(a => a.rank === 1)
      .map(a => ({ team, award: findAward(a.award_id) }))
    )
    .filter('award')
    .orderBy([w => w.award!.id !== 1, w => w.award!.name])
    .value()
  )

  /** Any submitted film from any year, weighted so every film is equally likely. */
  const randomFilmPath = () => {
    const picks = festivals.value.flatMap(festival =>
      festival.teams.map(t => archiveFilmPath(festivalYear(festival), t.id))
    )
    return _.sample(picks)
  }

  return {
    year,
    festivals,
    winners,
    filmCount,
    covers,
    studios,
    studio,
    studioSummary,
    studioLoading,
    searchTerm,
    searching,
    searchLoading,
    searchResults,
    awards,
    filmFestival,
    teams,
    yearLoading,
    team,
    description,
    neighbors,
    findAward,
    yearWinners,
    randomFilmPath,
  }
})

const ArchivePoster = gql(`fragment ArchivePoster on teams @_unmask {
  poster: assets(where: {asset_type_id: {_eq: 21}}, order_by: {created: desc}, limit: 1) {
    id
    file_name
  }
}`)

const ArchiveFilm = gql(`fragment ArchiveFilm on teams @_unmask {
  film {
    id
    mux_playback_id
    mux_thumbnail_time
  }
  studio {
    id
    name
  }
}`)

export const ArchiveCard = gql(
  `fragment ArchiveCard on teams @_unmask {
  id
  name
  film_name
  genre
  ...ArchivePoster
  ...ArchiveFilm
  awards: award_wins(order_by: {rank: asc}) {
    id
    rank
    award_id
  }
}`,
  [ArchivePoster, ArchiveFilm],
)

const ArchiveIndexQuery = gql(
  `query ArchiveIndexQuery {
  film_festivals(order_by: {id: desc}) {
    id
    name
    is_fm
    teams(where: {submitted_at: {_is_null: false}}) {
      id
    }
    winners: teams(where: {submitted_at: {_is_null: false}, award_wins: {award_id: {_eq: 1}, rank: {_eq: 1}}}) {
      id
      name
      film_name
      genre
      ...ArchivePoster
      ...ArchiveFilm
    }
  }
}`,
  [ArchivePoster, ArchiveFilm],
)

const ArchiveYearQuery = gql(
  `query ArchiveYearQuery($id: numeric!) {
  awards {
    id
    name
  }
  film_festivals(where: {id: {_eq: $id}}) {
    id
    name
    is_fm
    teams(where: {submitted_at: {_is_null: false}}, order_by: {film_name: asc}) {
      team_number
      ...ArchiveCard
    }
  }
}`,
  [ArchiveCard],
)

const ArchiveCoversQuery = gql(
  `query ArchiveCoversQuery($ids: [numeric!]!) {
  film_festivals {
    id
    covers: teams(where: {id: {_in: $ids}, submitted_at: {_is_null: false}, assets: {asset_type_id: {_eq: 21}}}) {
      id
      ...ArchivePoster
    }
  }
}`,
  [ArchivePoster],
)

const ArchiveSearchQuery = gql(
  `query ArchiveSearchQuery($q: String!, $limit: Int!) {
  film_festivals(order_by: {id: desc}) {
    id
    teams(
      where: {submitted_at: {_is_null: false}, _or: [{film_name: {_ilike: $q}}, {name: {_ilike: $q}}, {studio: {name: {_ilike: $q}}}]}
      order_by: {film_name: asc}
      limit: $limit
    ) {
      ...ArchiveCard
    }
  }
}`,
  [ArchiveCard],
)

const ArchiveFilmQuery = gql(`query ArchiveFilmQuery($festivalId: numeric!, $teamId: numeric!) {
  film_festivals(where: {id: {_eq: $festivalId}}) {
    id
    teams(where: {id: {_eq: $teamId}, submitted_at: {_is_null: false}}) {
      id
      description
    }
  }
}`)

const ArchiveStudiosQuery = gql(
  `query ArchiveStudiosQuery {
  studios(where: {teams: {submitted_at: {_is_null: false}}}, order_by: {name: asc}) {
    id
    name
    teams(where: {submitted_at: {_is_null: false}}) {
      id
      film_festival_id
      awards: award_wins {
        id
        rank
      }
    }
    latest: teams(
      where: {submitted_at: {_is_null: false}, assets: {asset_type_id: {_eq: 21}}}
      order_by: {film_festival_id: desc}
      limit: 1
    ) {
      id
      ...ArchivePoster
    }
  }
}`,
  [ArchivePoster],
)

const ArchiveStudioQuery = gql(
  `query ArchiveStudioQuery($id: Int!) {
  awards {
    id
    name
  }
  studios(where: {id: {_eq: $id}}) {
    id
    name
    slogan
    teams(where: {submitted_at: {_is_null: false}}, order_by: [{film_festival_id: desc}, {film_name: asc}]) {
      film_festival_id
      team_number
      description
      ...ArchiveCard
    }
  }
}`,
  [ArchiveCard],
)
